/**
 * React Query mutate() allows concurrent calls. Button disabled={isPending}
 * only helps AFTER re-render, so a double-click / Enter can create twice.
 * This wrapper locks synchronously before the first request leaves.
 */
import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";

export function useSingleFlightMutation(options) {
  const lockRef = useRef(false);
  const mutation = useMutation(options);

  const mutate = (variables, mutateOptions) => {
    if (lockRef.current || mutation.isPending) return;
    lockRef.current = true;
    mutation.mutate(variables, {
      ...mutateOptions,
      onSettled: (data, error, vars, context) => {
        lockRef.current = false;
        mutateOptions?.onSettled?.(data, error, vars, context);
      },
    });
  };

  const mutateAsync = async (variables, mutateOptions) => {
    if (lockRef.current || mutation.isPending) {
      throw new Error("mutation_in_flight");
    }
    lockRef.current = true;
    try {
      return await mutation.mutateAsync(variables, mutateOptions);
    } finally {
      lockRef.current = false;
    }
  };

  return {
    ...mutation,
    mutate,
    mutateAsync,
    isPending: mutation.isPending || lockRef.current,
  };
}
